import { AIRPORT_CODES_BY_ZIP } from '@/constants/airportCodes';

import { CARRIER_CSV_COLUMNS, CSV_HEADER_ALIASES, CONFLICT_ACTIONS, DEFAULT_LEG } from './constants';

const collapse = (value) =>
    (value ?? '')
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, ' ');

// header alias → row field, built once from CSV_HEADER_ALIASES.
const HEADER_LOOKUP = Object.entries(CSV_HEADER_ALIASES).reduce((acc, [field, aliases]) => {
    aliases.forEach((alias) => {
        acc[collapse(alias)] = field;
    });
    return acc;
}, {});

/**
 * Reverse of AIRPORT_CODES_BY_ZIP: one representative zip per airport code.
 *
 * Coverage has no column of its own — the Accounts page derives a carrier's airport
 * codes from each location's business_zip. So to record "this carrier covers DFW" we
 * have to store a zip that maps back to DFW. First zip wins, which keeps the choice
 * deterministic across renders.
 */
export const ZIP_BY_AIRPORT_CODE = Object.entries(AIRPORT_CODES_BY_ZIP).reduce((acc, [zip, code]) => {
    if (code && !acc[code]) acc[code] = zip;
    return acc;
}, {});

export const zipForAirportCode = (code) => ZIP_BY_AIRPORT_CODE[(code || '').toUpperCase()] || null;

/** Split a typed "DFW, ONT" field into valid three-letter codes we actually know. */
export const parseAirportCodes = (value) => {
    const codes = (Array.isArray(value) ? value : (value ?? '').toString().split(/[,;/|]/))
        .map((code) => (code ?? '').toString().trim().toUpperCase())
        .filter((code) => /^[A-Z]{3}$/.test(code));
    return [...new Set(codes)];
};

/**
 * Airport codes split into the ones we can record as coverage and the ones we can't.
 * A code with no zip in the lookup is reported back to the operator rather than
 * dropped, so "I typed DFW and it vanished" never happens silently.
 */
export const resolveAirportCoverage = (value) => {
    const codes = parseAirportCodes(value);
    const airport_codes = [];
    const location_zips = [];
    const unknown = [];
    codes.forEach((code) => {
        const zip = zipForAirportCode(code);
        if (zip) {
            airport_codes.push(code);
            location_zips.push(zip);
        } else {
            unknown.push(code);
        }
    });
    return { airport_codes, location_zips, unknown };
};

const cell = (row, field) => {
    const key = Object.keys(row || {}).find((k) => HEADER_LOOKUP[collapse(k)] === field);
    return key == null ? '' : (row[key] ?? '').toString().trim();
};

/**
 * One papaparse (header:true) row → the payload shape both endpoints accept. Returns
 * null for a row with nothing in it, so trailing blank lines don't become carriers.
 *
 * `leg` is the leg picked for the upload as a whole and applies to every row. Nothing in
 * the file can override it: see CSV_HEADER_ALIASES for why a Leg column is not read.
 *
 * Deliberately no validation here: the backend owns what makes a row valid (and has
 * to, since it re-validates on commit). This is transport shaping only.
 */
export const parseCarrierCsvRow = (row, leg = DEFAULT_LEG) => {
    const business_name = cell(row, 'business_name');
    const dot_number = cell(row, 'dot_number');
    const email = cell(row, 'email');
    if (!business_name && !dot_number && !email) return null;

    const { airport_codes, location_zips } = resolveAirportCoverage(cell(row, 'airport_codes'));

    return {
        business_name,
        alias: cell(row, 'alias'),
        dot_number,
        email,
        mc_num: cell(row, 'mc_num'),
        business_address: cell(row, 'business_address'),
        business_city: cell(row, 'business_city'),
        business_state: cell(row, 'business_state'),
        business_zip: cell(row, 'business_zip'),
        airport_codes,
        location_zips,
        leg: leg || DEFAULT_LEG,
    };
};

export const parseCarrierCsvRows = (rows, leg = DEFAULT_LEG) =>
    (rows || []).map((row) => parseCarrierCsvRow(row, leg)).filter(Boolean);

/**
 * Airport codes across a CSV upload that couldn't be resolved to coverage, deduped.
 * `parseCarrierCsvRow` drops these per row so the payload only carries codes we can
 * actually store — this is how the review step surfaces them instead, mirroring the
 * `unknown` array the manual-invite field already shows under its input.
 */
export const unknownAirportCodesInCsvRows = (rows = []) => {
    const unknown = new Set();
    (rows || []).forEach((row) => {
        resolveAirportCoverage(cell(row, 'airport_codes')).unknown.forEach((code) => unknown.add(code));
    });
    return [...unknown];
};

/** Prefix for a commit notification when earlier rows couldn't be read or had airport
 * codes we couldn't resolve — folded into the final summary so it isn't silently
 * overwritten by the single-slot notification the commit fires next. */
export const readErrorsNote = (errors = []) => {
    if (!errors.length) return '';
    return `${errors.length} row${errors.length === 1 ? '' : 's'} couldn't be read — ${
        errors[0]?.errors?.[0] || 'check the file'
    }`;
};

/**
 * Why an upload produced nothing. A file whose columns don't match the template parses
 * to zero usable rows, and saying only "no usable rows" leaves the operator with no
 * idea why — so name the headers we saw next to the ones we needed.
 */
export const noUsableRowsMessage = (headers = []) => {
    const found = (headers || []).map((h) => (h ?? '').toString().trim()).filter(Boolean);
    if (!found.length) return 'That file has no header row — the first row must name the columns.';

    const recognized = found.filter((h) => !!HEADER_LOOKUP[collapse(h)]);
    if (!recognized.length) {
        return `None of those columns were recognized (${found.join(', ')}). Expected: ${CARRIER_CSV_COLUMNS.join(
            ', '
        )}.`;
    }
    return 'No usable rows — each row needs at least a carrier name, DOT or invite email.';
};

/** The downloadable sample: the specified header plus one filled-in example row. */
export const buildCarrierCsvSample = () => {
    const example = [
        'Acme Final Mile',
        'Acme DFW',
        '1234567',
        'ops@acmefinalmile.com',
        '100 Airport Rd',
        'Irving',
        'TX',
        '75061',
    ];
    return `${CARRIER_CSV_COLUMNS.join(',')}\n${example.join(',')}\n`;
};

/**
 * One row of the Method B form → the same payload shape a CSV row produces, so both
 * methods hit the endpoints identically.
 */
export const buildManualRow = (form = {}) => {
    const { airport_codes, location_zips } = resolveAirportCoverage(form.airport_codes);
    return {
        business_name: (form.business_name || '').trim(),
        alias: (form.alias || '').trim(),
        dot_number: (form.dot_number || '').trim(),
        email: (form.email || '').trim(),
        business_address: '',
        business_city: '',
        business_state: '',
        business_zip: '',
        airport_codes,
        location_zips,
        leg: form.leg || DEFAULT_LEG,
    };
};

/** Enough of the form filled in to be worth sending. */
export const isManualRowSubmittable = (form = {}) => !!(form.business_name || '').trim();

/**
 * Split a match response into the rows that commit straight through and the rows that
 * need an operator decision.
 *
 * Only `invite` (matched nothing) goes straight through. Every DOT match is a decision,
 * including `connected`: a DOT already wired to this org may still be a new location
 * the operator wants added. `duplicate` (same DOT as an earlier row in this file) is a
 * decision too, or one upload would create two accounts on a DOT. `error` rows are
 * surfaced separately: they can't be committed, and dropping them quietly would lose
 * part of an upload.
 */
export const splitMatchedRows = (rows = []) => ({
    ready: (rows || []).filter((r) => r.action === 'invite'),
    conflicts: (rows || []).filter((r) => ['connect', 'conflict', 'connected', 'duplicate'].includes(r.action)),
    errors: (rows || []).filter((r) => r.action === 'error'),
});

/**
 * Commit payload for a row that matched nothing: create and invite.
 *
 * `sourceRow` is the row we originally uploaded, not anything the match response gave
 * back — the response carries verdicts only, so every field the operator supplied
 * (coverage zips included) survives the round trip intact.
 */
export const commitRowFor = (matched, sourceRow = {}) => ({ ...sourceRow, action: 'INVITE' });

/**
 * Commit payload for a flagged row the operator has ruled on.
 * - CONNECT links the existing account the operator picked (`client_id`) to this org.
 *   The backend re-checks that it actually matched this row's DOT.
 * - ADD_ANYWAY invites a separate account despite the DOT already being on Onward —
 *   the "same DOT, different location" case.
 * - SKIP leaves the row alone entirely.
 *
 * Anything else is SKIP: the modal won't commit until every row is decided, so an
 * absent action can only mean a stale resolution, and doing nothing is the safe read.
 *
 * Only the name comes from the resolution. DOT is read-only in the modal: it's what
 * the row was matched on, and an edited one would commit without ever being re-checked.
 */
export const commitRowForConflict = (resolution = {}, sourceRow = {}) => {
    const base = { ...sourceRow, business_name: resolution?.business_name ?? sourceRow.business_name };

    if (resolution?.action === CONFLICT_ACTIONS.CONNECT && resolution.client_id) {
        return { ...base, action: 'CONNECT', client_id: resolution.client_id };
    }
    if (resolution?.action === CONFLICT_ACTIONS.ADD_ANYWAY) return { ...base, action: 'INVITE' };
    return { ...base, action: 'SKIP' };
};

/**
 * What the batch will actually do once committed: accounts created and accounts
 * connected.
 *
 * Not the row count: a flagged row resolved as Skip does nothing. Only `invite` rows
 * and Add Anyway resolutions produce an account, and only Connect resolutions link
 * one — which is what the confirm button has to count, since labelling a batch of
 * skips "Add 1 carrier" states the opposite of what pressing it does.
 */
export const plannedCounts = (ready = [], conflicts = [], resolutions = {}) => {
    const actionOf = (c) => resolutions?.[c.index]?.action;
    return {
        adds:
            (ready || []).filter((r) => r.action === 'invite').length +
            (conflicts || []).filter((c) => actionOf(c) === CONFLICT_ACTIONS.ADD_ANYWAY).length,
        connects: (conflicts || []).filter((c) => actionOf(c) === CONFLICT_ACTIONS.CONNECT).length,
    };
};

/** Confirm-button label for the exceptions modal, e.g. "Add 2 · Connect 1". */
export const confirmLabel = ({ adds = 0, connects = 0 } = {}) => {
    const parts = [];
    if (adds) parts.push(`Add ${adds}`);
    if (connects) parts.push(`Connect ${connects}`);
    if (!parts.length) return 'Done';
    if (parts.length === 1) return `${parts[0]} carrier${adds + connects === 1 ? '' : 's'}`;
    return parts.join(' · ');
};

/** Every flagged row has been decided, so the batch can commit. */
export const allConflictsResolved = (conflicts = [], resolutions = {}) =>
    (conflicts || []).every((c) => !!resolutions[c.index]?.action);

/**
 * Human summary of a commit response, for the page snackbar. Ordered by how much the
 * operator cares: what failed, then what happened.
 */
export const summarizeCommit = (counts = {}) => {
    const parts = [];
    const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
    if (counts.invited) parts.push(`${plural(counts.invited, 'carrier')} invited`);
    if (counts.created) parts.push(`${plural(counts.created, 'carrier')} added without an invite`);
    if (counts.connected) parts.push(`${plural(counts.connected, 'carrier')} connected`);
    if (counts.already_connected) parts.push(`${plural(counts.already_connected, 'carrier')} already connected`);
    if (counts.skipped) parts.push(`${plural(counts.skipped, 'row')} skipped`);
    if (counts.error) parts.push(`${plural(counts.error, 'row')} failed`);
    if (!parts.length) return 'Nothing to add';
    return parts.join(', ');
};

/** A commit is a failure worth flagging in red only if nothing succeeded. */
export const commitSeverity = (counts = {}) => {
    const succeeded = (counts.invited || 0) + (counts.created || 0) + (counts.connected || 0);
    if (counts.error && !succeeded) return 'error';
    if (counts.error) return 'warning';
    return 'success';
};
